import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatRp, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function CartPage() {
  const { items, updateQty, removeItem, cartTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center">
        <ShoppingBag className="mx-auto brand-text-primary" size={48} />
        <h2 className="font-display text-2xl font-bold mt-4">Login untuk Melihat Keranjang</h2>
        <p className="text-sm text-[#5C5C5C] mt-2">Anda perlu masuk untuk menyimpan produk di keranjang.</p>
        <Link to="/login"><Button className="mt-5 brand-btn-primary rounded-xl">Masuk Sekarang</Button></Link>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center">
        <ShoppingBag className="mx-auto brand-text-primary" size={48} />
        <h2 className="font-display text-2xl font-bold mt-4">Keranjang Kosong</h2>
        <p className="text-sm text-[#5C5C5C] mt-2">Yuk mulai belanja produk favoritmu!</p>
        <Link to="/katalog"><Button className="mt-5 brand-btn-primary rounded-xl">Lihat Produk</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-6" data-testid="cart-title">Keranjang Belanja</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-3">
          {items.map((item) => {
            const p = item.product;
            const price = p.sale_price || p.price;
            return (
              <div key={item.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 flex gap-3 sm:gap-4" data-testid={`cart-item-${item.id}`}>
                <Link to={`/produk/${p.slug}`} className="shrink-0">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-[#FAF9F6] overflow-hidden">
                    {p.images?.[0] && <img src={imageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/produk/${p.slug}`} className="font-semibold text-sm sm:text-base line-clamp-2 hover:brand-text-primary">
                    {p.name}
                  </Link>
                  <div className="text-xs text-[#5C5C5C] mt-1">Stok: {p.stock} {p.unit}</div>
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="brand-text-primary font-bold">{formatRp(price)}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-[#FAF9F6] border border-[#E5E5E5] rounded-lg">
                        <button onClick={() => updateQty(item.id, item.product_id, Math.max(1, item.quantity - 1))} className="w-8 h-8 grid place-items-center">
                          <Minus size={12} />
                        </button>
                        <div className="w-8 text-center text-sm font-bold">{item.quantity}</div>
                        <button onClick={() => updateQty(item.id, item.product_id, Math.min(p.stock, item.quantity + 1))} className="w-8 h-8 grid place-items-center">
                          <Plus size={12} />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.id)} data-testid={`remove-item-${item.id}`} className="text-[#5C5C5C] hover:brand-text-primary p-1">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <aside className="bg-white border border-[#E5E5E5] rounded-2xl p-5 h-fit sticky top-24">
          <h3 className="font-display text-xl font-bold mb-4">Ringkasan</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#5C5C5C]">Subtotal</span><span className="font-semibold">{formatRp(cartTotal)}</span></div>
            <div className="flex justify-between text-xs text-[#5C5C5C]"><span>Ongkir</span><span>Dihitung saat checkout</span></div>
          </div>
          <div className="border-t border-[#E5E5E5] my-4" />
          <div className="flex justify-between items-baseline">
            <span className="font-semibold">Total</span>
            <span className="font-display font-black text-2xl brand-text-primary" data-testid="cart-total">{formatRp(cartTotal)}</span>
          </div>
          <Button
            onClick={() => navigate("/checkout")}
            className="mt-4 w-full h-12 brand-btn-primary rounded-xl font-bold"
            data-testid="checkout-btn"
          >
            Lanjut Checkout
          </Button>
          <Link to="/katalog" className="block text-center text-sm text-[#5C5C5C] hover:brand-text-primary mt-3">Lanjut Belanja</Link>
        </aside>
      </div>
    </div>
  );
}
